import Cookies from "js-cookie";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchAllUserBasedData } from "../api/authorizeApi";
import useApiResponse from "../customHooks/useApiResponse";
interface AuthCheckProviderProps {
  children: ReactNode;
}
interface AuthCheckContextProps {
  userDetails?: Record<string, any> | null;
  isAuthorized: () => boolean;
  loggedIn: (data: Record<string, any>) => void;
  loggedOut: () => void;
  updateUserDetails: (data: Record<string, any>) => void;
}
const AuthCheckContext = createContext<AuthCheckContextProps | null>(null);

export const AuthCheckProvider = ({ children }: AuthCheckProviderProps) => {
  const { apiResponse } = useApiResponse();
  const [authorized, setAuthorized] = useState(true);
  const [userDetails, setUserDetails] = useState<Record<string, any> | null>(
    null
  );

  const loggedOut = useCallback(() => {
    setAuthorized(false);
    setUserDetails(null);
    Cookies.remove("energy_box_t");
  }, []);

  const getCurrentUserDetails = async () => {
    const user = await apiResponse(await fetchAllUserBasedData("user-details"));
    if (user) {
      setUserDetails(user["user-details"] || []);
      setAuthorized(true);
    } else {
      loggedOut();
    }
  };

  useEffect(() => {
    const token = Cookies.get("energy_box_t");
    if (token) {
      setAuthorized(true);
      getCurrentUserDetails();
    } else {
      loggedOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loggedIn = useCallback((data: Record<string, any>) => {
    if (data) {
      Cookies.set("energy_box_t", data.token);
      setAuthorized(true);
      setUserDetails(data.user);
    }
  }, []);

  const isAuthorized = useCallback(() => authorized, [authorized]);

  const updateUserDetails = useCallback((data: Record<string, any>) => {
    setUserDetails(data || null);
  }, []);

  // Memoized so context consumers only re-render when auth state changes.
  const contextValue = useMemo(
    () => ({
      userDetails,
      isAuthorized,
      loggedIn,
      loggedOut,
      updateUserDetails,
    }),
    [userDetails, isAuthorized, loggedIn, loggedOut, updateUserDetails]
  );

  return (
    <AuthCheckContext.Provider value={contextValue}>
      {children}
    </AuthCheckContext.Provider>
  );
};

export const useAuthCheck = () => {
  return useContext(AuthCheckContext);
};
